import { City } from 'country-state-city'
import {
  DEFAULT_STATE,
  DEFAULT_SUPPORTED_STATES,
  LOCATION_COUNTRY_CODE
} from '../consts/ProviderConsts'

const extractStateCode = entry => {
  const code = typeof entry === 'string' ? entry : entry?.code
  return typeof code === 'string' ? code.trim().toUpperCase() : null
}

/**
 * Normalize the PROVIDERS_SEARCH_SUPPORTED_STATES app config value into state codes.
 * The config row is hand-edited, so a bare ['OH', 'AZ'] is accepted alongside the
 * documented { states: [{ code: 'OH' }] } shape.
 * @param {Object|Array} rawConfig - The app config value
 * @returns {Array} Array of uppercase state codes, or the defaults when unusable
 */
export const normalizeSupportedStates = rawConfig => {
  const entries = Array.isArray(rawConfig) ? rawConfig : rawConfig?.states

  if (!Array.isArray(entries)) {
    return DEFAULT_SUPPORTED_STATES
  }

  const stateCodes = entries
    .map(extractStateCode)
    .filter((code, index, codes) => !!code && codes.indexOf(code) === index)

  return stateCodes.length ? stateCodes : DEFAULT_SUPPORTED_STATES
}

/**
 * Resolve the state to search when the member has not chosen one
 * @param {Object|Array} rawConfig - The app config value
 * @param {Array} supportedStates - Normalized state codes
 * @returns {string} The default state code
 */
export const normalizeDefaultState = (rawConfig, supportedStates) => {
  const configuredDefault = extractStateCode(rawConfig?.default_state)

  if (configuredDefault && supportedStates.includes(configuredDefault)) {
    return configuredDefault
  }

  return supportedStates[0] || DEFAULT_STATE
}

/**
 * States that should show a directory banner, keyed by state code.
 * Only entries with display_banner: true are included; missing means no banner.
 * @param {Object|Array} rawConfig - The app config value
 * @returns {Object} Map of state code to { bannerLink }
 */
export const getDisplayBanners = rawConfig => {
  const entries = Array.isArray(rawConfig) ? rawConfig : rawConfig?.states

  if (!Array.isArray(entries)) {
    return {}
  }

  return entries.reduce((banners, entry) => {
    if (!entry || typeof entry !== 'object' || entry.display_banner !== true) {
      return banners
    }

    const code = extractStateCode(entry)
    if (!code) {
      return banners
    }

    banners[code] = {
      bannerLink:
        typeof entry.banner_link === 'string' ? entry.banner_link.trim() : ''
    }
    return banners
  }, {})
}

/**
 * Resolve PROVIDERS_SEARCH_RESOURCES for the selected directory state.
 *
 * Any key under overrides_by_state[state] replaces the matching default,
 * including empty arrays/objects (e.g. GA with no links). Keys omitted from
 * the override keep the top-level value. The overrides map itself is stripped.
 *
 * @param {Object} resources - The app config value
 * @param {string} stateCode - Selected state code (e.g. 'OH', 'GA')
 * @returns {Object} Resources for that state, without overrides_by_state
 */
export const getResourcesForState = (resources, stateCode) => {
  if (!resources || typeof resources !== 'object' || Array.isArray(resources)) {
    return {
      external_links: [],
      popular_search_data: [],
      external_docs: {}
    }
  }

  const { overrides_by_state, ...defaults } = resources
  const code =
    typeof stateCode === 'string' ? stateCode.trim().toUpperCase() : null
  const override = code ? overrides_by_state?.[code] : null

  if (!override || typeof override !== 'object' || Array.isArray(override)) {
    return defaults
  }

  return { ...defaults, ...override }
}

/**
 * Get cities for a specific state code
 * @param {string} stateCode - The state code (e.g., 'OH', 'AZ')
 * @returns {Array} Array of city objects with value and label properties
 */
export const getCitiesByState = stateCode => {
  let citiesArray = City.getCitiesOfState(LOCATION_COUNTRY_CODE, stateCode)
    ?.filter(city => !city.name.includes('County'))
    .map(city => ({
      value: city?.name + ',' + city?.stateCode,
      label: city?.name
    }))
  return citiesArray || []
}

/**
 * Get all cities from the supported states
 * @param {Array} supportedStates - State codes to collect cities for
 * @returns {Array} Array of all city objects from the supported states
 */
export const getAllCities = (supportedStates = DEFAULT_SUPPORTED_STATES) => {
  return supportedStates.map(stateCode => getCitiesByState(stateCode)).flat()
}
